import React from 'react';
import { QuestionCircleOutline as QuestionCircleOutlined, } from '@ant-design/icons';
import clsx from 'clsx';
import {
    Checkbox,
    Col,
    Input as AntInput,
    InputNumber,
    Radio,
    Row,
    Select,
    Button,
    Tooltip,
} from 'antd';

export function Title({ title, required = false, tooltip }) {
    if (title === null) {
        return null;
    }
    return (
        <div className={`input-title-name ${required ? 'required-field' : ''}`}>
            {title}
            {tooltip ? (
                <Tooltip title={tooltip}>
                    {' '}
                    <QuestionCircleOutlined />
                </Tooltip>
            ) : null}
        </div>
    );
}

export function RenderRadioGroup({
    radioItems,
    labelKey,
    valueKey,
    meta,
    input,
    isVertical,
    errorText,
    disabledOptions = false,
    disabledOptionsList = [],
    ...rest
}) {
    return (
        <>
            <Radio.Group {...input} {...rest}>
                <Row type="flex">
                    {radioItems.map((item) => (
                        <Col key={item[valueKey]} span={isVertical ? 24 : undefined}>
                            <Radio
                                value={item[valueKey]}
                                className="radio-item"
                                disabled={disabledOptions || disabledOptionsList.includes(item[valueKey])}
                            >
                                <div className="availability-status-radio">
                                    {item.icon ? (
                                        <img
                                            src={item.icon}
                                            loading="lazy"
                                            alt="React Logo"
                                            className="radio-item-icon"
                                        />
                                    ) : (
                                        ''
                                    )}{' '}
                                    <span className="radio-item-label status-radio-label">{item[labelKey]}</span>
                                </div>
                            </Radio>
                        </Col>
                    ))}
                </Row>
            </Radio.Group>
            {(meta.error && meta.touched) || Boolean(errorText) ? (
                <span className="form-control-feedback">{meta.error || errorText}</span>
            ) : null}
            {/* {meta.error && meta.touched && <span className="form-control-feedback">{meta.error}</span>} */}
        </>
    );
}

export function RenderCheckboxField({
    title,
    meta,
    input,
    name,
    required,
    isInline,
    errorText,
    ...rest
}) {
    const titleComponent = <Title title={title} required={required} />;
    return (
        <div className="input-group" data-cy={rest['data-cy']}>
            {isInline ? (
                <Checkbox checked={input.value} {...input} {...rest} style={{ width: '100%' }} name={name}>
                    {titleComponent}
                </Checkbox>
            ) : (
                <>
                    {' '}
                    {titleComponent}
                    <Checkbox
                        checked={input.value}
                        {...input}
                        {...rest}
                        style={{ width: '100%' }}
                        name={name}
                    />
                </>
            )}
            {(meta.error && meta.touched) || Boolean(errorText) ? (
                <div className="form-control-feedback">{meta.error || errorText}</div>
            ) : null}
        </div>
    );
}

const replaceNonFloat = (input) => {
  if (input === '') {
    return 0;
  }
  return input.replace(/[^\w.-]+/g, '');
};

export const RenderFloatField = ({
    title,
    meta,
    input,
    max,
    required,
    inputGroupClassName,
    hideArrows,
    errorText,
    highlightError,
    absolutePositionedErrorMsg,
    precision,
    disabled = false,
    ...rest
}) => {
    return (
        <div
            className={clsx('input-group', inputGroupClassName, {
                'input-group--relative-positioned': absolutePositionedErrorMsg,
            })}
        >
            <Title title={title} required={required} />
            <InputNumber
                {...input}
                min={0}
                disabled={disabled}
                max={max}
                parser={replaceNonFloat}
                precision={precision}
                {...rest}
                className={clsx('number-field', rest.className, {
                    'number-field--hidden-arrows': hideArrows,
                    'number-field--highlight-error': meta.error && highlightError,
                })}
            />
            {meta.error && meta.touched && (
                <span
                    className={clsx('form-control-feedback', {
                        'form-control-feedback--absolute-positioned': absolutePositionedErrorMsg,
                    })}
                >
                    {meta.error}
                </span>
            )}
            {Boolean(errorText) ? <div className="form-control-feedback">{errorText}</div> : null}
        </div>
    );
}

const capitalizeFirstLetter = (string) => {
    return string.charAt(0).toUpperCase() + string.slice(1);
}

export const RenderSelectField = React.forwardRef(
    (
        {
            title,
            meta,
            input,
            selectOptions,
            allowClear,
            labelKey,
            tooltip,
            secondaryLabelKey,
            valueKey,
            descriptionKey,
            mode,
            errorText,
            disabled,
            required,
            placeholder,
            inputGroupClassName,
            absolutePositionedErrorMsg,
            className = '',
            filterSelectOption,
            dropdownStyle = '',
            ...rest
        },
        ref,
    ) => (
        <div
            className={clsx('input-group', inputGroupClassName, {
                'input-group--relative-positioned': absolutePositionedErrorMsg,
            })}
            data-heap-redact-text
        >
            {rest.isTooltipInfo ? (
                <InfoTitle title={title} required={required} tooltip={tooltip} />
            ) : (
                <Title title={title} required={required} tooltip={tooltip} />
            )}

            <Select
                size="large"
                {...input}
                {...rest}
                ref={ref}
                allowClear={allowClear}
                mode={mode}
                placeholder={!placeholder ? `Please Select ${title || ''}` : placeholder}
                showSearch
                disabled={disabled}
                className={clsx('select-field', className)}
                filterOption={filterSelectOption}
                dropdownStyle={dropdownStyle}
            >
                {(selectOptions || []).map((option) => {
                    if (typeof option === 'string' || typeof option === 'number') {
                        return (
                            <Option key={option} value={option} data-heap-redact-text>
                                {typeof option === 'number' ? option : capitalizeFirstLetter(option)}
                            </Option>
                        );
                    }
                    let { label } = option;
                    if (labelKey) {
                        label = option[labelKey];
                    }
                    if (secondaryLabelKey && !label) {
                        label = option[secondaryLabelKey];
                    }
                    const value = valueKey ? option[valueKey] : option.value;
                    let description;
                    if (labelKey !== 'description') {
                        description = descriptionKey ? option[descriptionKey] : option.description;
                    }
                    let reactLabel = null;
                    if (description) {
                        reactLabel = (
                            <>
                                <p className="select-field-with-description--value">{label || value}</p>
                                <span className="select-field-with-description--description">{description}</span>
                            </>
                        );
                    }
                    return (
                        // `tags` mode require to have option value as string - https://github.com/ant-design/ant-design/issues/3991
                        <Option
                            key={value}
                            value={mode === 'tags' ? String(value) : value}
                            disabled={option.disabled}
                            data-heap-redact-text
                        >
                            {description ? reactLabel : capitalizeFirstLetter(label)}
                        </Option>
                    );
                })}
            </Select>
            {meta && (meta.submitError || meta.error) && meta.touched && (
                <span
                    className={clsx('form-control-feedback', {
                        'form-control-feedback--absolute-positioned': absolutePositionedErrorMsg,
                    })}
                >
                    {meta.submitError ? meta.submitError : meta.error}
                </span>
            )}
            {Boolean(errorText) ? <div className="form-control-feedback">{errorText}</div> : null}
        </div>
    ),
);


export function RenderTextArea({
    title,
    tooltip,
    meta,
    rows,
    input,
    required,
    errorText,
    inputGroupClassName,
    ...rest
}) {
    return (
        <div className={clsx('input-group', inputGroupClassName)} data-heap-redact-text>
            <Title title={title} required={required} tooltip={tooltip} />
            <AntInput.TextArea rows={rows} {...input} {...rest} />
            {(meta && meta.error && meta.touched) || Boolean(errorText) ? (
                <span className="form-control-feedback">{meta.error || errorText}</span>
            ) : null}
        </div>
    );
}


export function SubmitButton({ submitting, pristine, label, ...rest }) {
    return (
        <Button primary  htmlType="submit" label={label || 'Save Changes'} disabled={submitting || pristine} {...rest} />
    );
}

export function RenderAntInputTextField({
    title,
    meta,
    input,
    required,
    isInline,
    horizontal,
    vertical,
    inputGroupClassName,
    ...rest
}) {
    return (
        <div
            className={clsx(inputGroupClassName, {
                'input-inline-block': isInline,
                'input-group': !isInline,
                'ant-input-text-field--horizontal': horizontal,
            })}
        >
            <Title title={title} required={required} />
            <div className={clsx({ 'ant-input-text-field--vertical': vertical })}>
                <AntInput {...input} {...rest} />
                {meta.error && meta.touched && <span className="form-control-feedback">{meta.error}</span>}
            </div>
        </div>
    );
}